import { Check } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

type Platform = 'windows' | 'mac' | '';
type Processor = 'M1' | 'M2' | 'M3' | 'M4' | 'M5' | '';

interface FormData {
  name: string;
  email: string;
  firm: string;
  platform: Platform;
  ramAmount: string;
  graphicsCard: string;
  customGraphicsCard: string;
  processor: Processor;
  intendedUses: string[];
  customUse: string;
}

const GRAPHICS_CARDS = [
  'NVIDIA RTX 4090',
  'NVIDIA RTX 4080',
  'NVIDIA RTX 4070',
  'NVIDIA RTX 3080',
  'NVIDIA RTX 3070',
  'AMD RX 7900 XTX',
];

const INTENDED_USES = [
  'Litigation & Discovery',
  'Transactions & Contracts',
  'Regulatory & Compliance',
  'Intellectual Property',
  'Research & Analysis',
  'Document Automation',
];

export function BetaSignup() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    firm: '',
    platform: '',
    ramAmount: '',
    graphicsCard: '',
    customGraphicsCard: '',
    processor: '',
    intendedUses: [],
    customUse: '',
  });

  // Generate unique IDs for form fields
  const nameId = useId();
  const emailId = useId();
  const firmId = useId();
  const customUseId = useId();

  // Load saved form data on mount
  useEffect(() => {
    const saved = localStorage.getItem('betaFormDraft');
    if (saved) {
      try {
        const parsedData = JSON.parse(saved);
        setFormData(parsedData);
      } catch (e) {
        console.error('Failed to load saved form data:', e);
      }
    }
  }, []);

  // Save form data to localStorage on changes
  useEffect(() => {
    if (Object.values(formData).some((v) => v !== '' && v.length !== 0)) {
      localStorage.setItem('betaFormDraft', JSON.stringify(formData));
    }
  }, [formData]);

  const handleInputChange = (field: keyof FormData, value: string | boolean | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleIntendedUseChange = (use: string, checked: boolean) => {
    const newUses = checked
      ? [...formData.intendedUses, use]
      : formData.intendedUses.filter((u) => u !== use);

    handleInputChange('intendedUses', newUses);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast.error('Please complete all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to API endpoint
      // Map frontend form data to backend interface
      const backendData = {
        ...formData,
        betaAgreementAccepted: true, // Submission implies agreement to beta terms
        betaAgreementName: formData.name, // Use applicant name as signatory
      };

      const response = await fetch('/api/beta-applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error messages from backend
        if (result.errors && Array.isArray(result.errors)) {
          for (const err of result.errors) {
            toast.error(err);
          }
        } else {
          toast.error(result.error || 'Failed to submit application');
        }
        return;
      }

      if (result.success) {
        toast.success('Application submitted successfully!');
        toast.info(
          'Our team will review your submission and contact you within 5-7 business days.',
        );

        // Clear saved draft
        localStorage.removeItem('betaFormDraft');

        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          firm: '',
          platform: '',
          ramAmount: '',
          graphicsCard: '',
          customGraphicsCard: '',
          processor: '',
          intendedUses: [],
          customUse: '',
        });
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      toast.error('There was an error submitting your application. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const hasBasicInfo = formData.name.trim() && formData.email.trim() && formData.firm.trim();

    const hasPlatform = formData.platform !== '';

    const hasRam = formData.ramAmount !== '';

    const hasHardwareInfo =
      formData.platform === 'mac'
        ? formData.processor !== ''
        : formData.graphicsCard !== '' || formData.customGraphicsCard.trim() !== '';

    const hasIntendedUses = formData.intendedUses.length > 0;

    return hasBasicInfo && hasPlatform && hasRam && hasHardwareInfo && hasIntendedUses;
  };

  return (
    <section id="beta-signup" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">
              Apply for Beta Program Access
            </h2>
            <p className="text-xl text-gray-600">
              Submit your application for early access to SafeIdea.ai. Our team will review each
              application and contact qualified candidates within 5-7 business days.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Basic Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor={nameId} className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id={nameId}
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id={emailId}
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor={firmId} className="block text-sm font-medium text-gray-700 mb-2">
                  Law Firm / Organization *
                </label>
                <input
                  type="text"
                  id={firmId}
                  required
                  value={formData.firm}
                  onChange={(e) => handleInputChange('firm', e.target.value)}
                  placeholder="e.g., Smith & Associates IP Law"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Platform Selection */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">System Information</h3>

              {/* Display selected system info */}
              {(formData.platform ||
                formData.ramAmount ||
                formData.graphicsCard ||
                formData.processor) && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">Your System:</p>
                  <div className="text-sm text-blue-800 space-y-1">
                    {formData.platform && (
                      <div>• Platform: {formData.platform === 'windows' ? 'Windows' : 'macOS'}</div>
                    )}
                    {formData.ramAmount && <div>• Memory: {formData.ramAmount}</div>}
                    {formData.platform === 'windows' && formData.graphicsCard && (
                      <div>
                        • Graphics Card:{' '}
                        {formData.graphicsCard === 'custom'
                          ? formData.customGraphicsCard || 'Custom'
                          : formData.graphicsCard}
                      </div>
                    )}
                    {formData.platform === 'mac' && formData.processor && (
                      <div>• Processor: Apple {formData.processor}</div>
                    )}
                  </div>
                </div>
              )}

              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-3">Platform *</legend>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="platform"
                      value="windows"
                      checked={formData.platform === 'windows'}
                      onChange={(e) => handleInputChange('platform', e.target.value as Platform)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Windows</strong> (Version 11 or newer required)
                    </span>
                  </label>

                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="platform"
                      value="mac"
                      checked={formData.platform === 'mac'}
                      onChange={(e) => handleInputChange('platform', e.target.value as Platform)}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Mac</strong> (macOS 12 or newer, Apple Silicon only)
                    </span>
                  </label>
                </div>
              </fieldset>

              {/* RAM Selection */}
              {formData.platform && (
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-3">
                    RAM Amount * <span className="text-gray-500">(32GB minimum)</span>
                  </legend>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['32GB', '48GB', '64GB', '64GB+'].map((ram) => (
                      <label key={ram} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="ram"
                          value={ram}
                          checked={formData.ramAmount === ram}
                          onChange={(e) => handleInputChange('ramAmount', e.target.value)}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{ram}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {/* Windows Graphics Card */}
              {formData.platform === 'windows' && (
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-3">
                    Graphics Card *
                  </legend>
                  <div className="space-y-3">
                    {GRAPHICS_CARDS.map((card) => (
                      <label key={card} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="graphics"
                          value={card}
                          checked={formData.graphicsCard === card}
                          onChange={(e) => handleInputChange('graphicsCard', e.target.value)}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{card}</span>
                      </label>
                    ))}

                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="graphics"
                        value="custom"
                        checked={formData.graphicsCard === 'custom'}
                        onChange={(e) => handleInputChange('graphicsCard', e.target.value)}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Other</span>
                    </label>

                    {formData.graphicsCard === 'custom' && (
                      <input
                        type="text"
                        placeholder="Enter your graphics card model"
                        value={formData.customGraphicsCard}
                        onChange={(e) => handleInputChange('customGraphicsCard', e.target.value)}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                  </div>
                </fieldset>
              )}

              {/* Mac Processor */}
              {formData.platform === 'mac' && (
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-3">
                    Processor * <span className="text-gray-500">(Apple Silicon only)</span>
                  </legend>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {['M1', 'M2', 'M3', 'M4', 'M5'].map((proc) => (
                      <label key={proc} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="processor"
                          value={proc}
                          checked={formData.processor === proc}
                          onChange={(e) =>
                            handleInputChange('processor', e.target.value as Processor)
                          }
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{proc}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>

            {/* Intended Use */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Intended Use</h3>

              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-3">
                  Select all workflows you're interested in: *
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {INTENDED_USES.map((use) => (
                    <label key={use} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.intendedUses.includes(use)}
                        onChange={(e) => handleIntendedUseChange(use, e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{use}</span>
                    </label>
                  ))}
                </div>

                <div className="mt-4">
                  <label
                    htmlFor={customUseId}
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Other uses you're interested in:
                  </label>
                  <textarea
                    id={customUseId}
                    rows={3}
                    value={formData.customUse}
                    onChange={(e) => handleInputChange('customUse', e.target.value)}
                    placeholder="Describe any other ways you plan to use SafeIdea.ai..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </fieldset>
            </div>

            {/* Beta Agreement */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">Beta Program Terms</h3>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-3">
                  Beta Program Benefits (Subject to Acceptance):
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Free access to the experimental platform</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Unlimited local AI with complete privacy</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>A few hours daily access to cloud AI at no charge</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Option to purchase additional cloud AI usage (passthrough from Anthropic
                      billing)
                    </span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Direct input on product development and features</span>
                  </li>
                </ul>

                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-xs text-amber-800">
                    <strong>Important:</strong> This is experimental pre-release beta software
                    provided "AS-IS" for testing purposes only with no guarantees of performance or
                    functionality. Beta participants assume all risk associated with the use of the
                    software during the beta testing period.
                  </p>
                </div>

                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded">
                  <p className="text-xs text-gray-700">
                    <strong>Note:</strong> Accepted beta participants will be required to sign a
                    non-disclosure agreement (NDA) and beta testing agreement before receiving
                    access to the software.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                size="lg"
                disabled={!isFormValid() || isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Beta Application'}
              </Button>

              {!isFormValid() && !isSubmitting && (
                <p className="text-sm text-gray-500 text-center mt-2">
                  Please complete all required fields.
                </p>
              )}

              <p className="text-xs text-gray-500 text-center mt-3">
                Applications are reviewed within 5-7 business days. You will be contacted regarding
                your application status.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
